import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { IActionWithPayload } from 'app/projects/core/src/lib/interfaces/action_with_payload.interface';
import { IEntityState } from 'app/projects/entity/src/lib/interfaces/entity.state.interface';
import { EntityState } from 'app/projects/entity/src/lib/models/entity.state';
import {
    AddPartialGrades,
    BulkUpdatePartialGrades,
    BulkUpdatePartialGradesFailure,
    BulkUpdatePartialGradesStatus,
    BulkUpdatePartialGradesStatusFailure,
    BulkUpdatePartialGradesStatusSuccess,
    BulkUpdatePartialGradesSuccess,
    DeletePartialGrade,
    DeletePartialGradeFailure,
    DeletePartialGradeSuccess,
    FetchPartialGrade,
    FetchPartialGradeFailure,
    FetchPartialGrades,
    FetchPartialGradesFailure,
    FetchPartialGradesSuccess,
    FetchPartialGradeSuccess,
    SetPartialGrade,
    SetPartialGrades,
    UnsetPartialGrade,
    UpdatePartialGrade,
    UpdatePartialGradeFailure,
    UpdatePartialGradeSuccess,
} from '../actions';
import { PartialGradeApiService } from '../services/partial-grade.api-service';
import { PartialGrade } from './partial-grade.model';
import { PartialGradeStateModel } from './partial-grade.state-model';

@State<PartialGradeStateModel>({
    name: 'partialGrade',
    defaults: new PartialGradeStateModel(),
})
@Injectable()
export class PartialGradeState extends EntityState<PartialGrade> implements IEntityState<PartialGrade> {
    @Selector()
    static getEntity(state: PartialGradeStateModel, id: string = state.item): PartialGrade {
        if (!state.map) {
            return null;
        }

        return state.map.get(id) || null;
    }

    @Selector()
    static getEntities(state: PartialGradeStateModel): PartialGrade[] {
        if (!state.map) {
            return null;
        }

        return Array.from(state.map.values());
    }

    @Selector()
    static getEntitiesAsMap(state: PartialGradeStateModel): Map<string, PartialGrade> {
        if (!state.map) {
            return null;
        }
        return state.map;
    }

    constructor(private _partialGradeService: PartialGradeApiService, private store: Store) {
        super(_partialGradeService, 30);

        this._propstoSearch = PartialGrade.props_to_search;
    }

    @Action(AddPartialGrades)
    addEntities(ctx: StateContext<PartialGradeStateModel>, action: AddPartialGrades): void {
        this._addEntities(ctx, action);
    }

    @Action(SetPartialGrade)
    setEntity(ctx: StateContext<PartialGradeStateModel>, action: SetPartialGrade): void {
        this._setEntity(ctx, action, AddPartialGrades);
    }

    @Action(UnsetPartialGrade)
    unsetEntity(ctx: StateContext<PartialGradeStateModel>): void {
        this._unsetEntity(ctx);
    }

    createEntity(ctx: StateContext<PartialGradeStateModel>, action: IActionWithPayload<PartialGrade>): void {
        throw new Error('Method not implemented.');
    }

    @Action(UpdatePartialGrade)
    updateEntity(ctx: StateContext<PartialGradeStateModel>, action: UpdatePartialGrade): void {
        this._updateEntity(ctx, action, UpdatePartialGradeSuccess, UpdatePartialGradeFailure, AddPartialGrades).then(() => {});
    }

    @Action(DeletePartialGrade)
    deleteEntity(ctx: StateContext<PartialGradeStateModel>, action: DeletePartialGrade): void {
        this._deleteEntity(ctx, action, DeletePartialGradeSuccess, DeletePartialGradeFailure).then(() => {});
    }

    @Action(FetchPartialGrade)
    fetchEntity(ctx: StateContext<PartialGradeStateModel>, action: FetchPartialGrade): void {
        this._fetchEntity(ctx, action, FetchPartialGradeSuccess, FetchPartialGradeFailure, AddPartialGrades, SetPartialGrade).then(() => {});
    }

    @Action(FetchPartialGradeSuccess)
    fetchEntitySuccess(ctx: StateContext<PartialGradeStateModel>, action: FetchPartialGradeSuccess): void {
        this._fetchEntitySuccess(ctx, action);
    }

    @Action(FetchPartialGradeFailure)
    fetchEntityFailure(ctx: StateContext<PartialGradeStateModel>, action: FetchPartialGradeFailure): void {
        this._fetchEntityFailure(ctx, action);
    }

    @Action(SetPartialGrades)
    setEntities(ctx: StateContext<PartialGradeStateModel>, action: SetPartialGrades): void {
        this._setEntities(ctx, action);
    }

    @Action(FetchPartialGrades)
    fetchEntities(ctx: StateContext<PartialGradeStateModel>): void {
        this._fetchEntities(ctx, FetchPartialGradesSuccess, FetchPartialGradesFailure, SetPartialGrades).then(() => {});
    }

    @Action(FetchPartialGradesSuccess)
    fetchEntitiesSuccess(ctx: StateContext<PartialGradeStateModel>): void {
        this._fetchEntitiesSuccess(ctx);
    }

    @Action(FetchPartialGradesFailure)
    fetchEntitiesFailure(ctx: StateContext<PartialGradeStateModel>): void {
        this._fetchEntitiesFailure(ctx);
    }

    @Action(BulkUpdatePartialGrades)
    bulkUpdatePartialGrades({ dispatch }: StateContext<PartialGradeStateModel>, { payload }: BulkUpdatePartialGrades): void {
        this._partialGradeService.bulkUpdate(payload).subscribe(
            (entities) => {
                dispatch([new AddPartialGrades(entities), new BulkUpdatePartialGradesSuccess(entities)]);
            },
            (response: HttpErrorResponse) => {
                dispatch(new BulkUpdatePartialGradesFailure(null));
            }
        );
    }

    @Action(BulkUpdatePartialGradesStatus)
    bulkUpdatePartialGradesStatus({ dispatch }: StateContext<PartialGradeStateModel>, { payload }: BulkUpdatePartialGradesStatus): void {
        this._partialGradeService.bulkUpdateStatus(payload.entities, payload.status).subscribe(
            (entities) => {
                dispatch([new AddPartialGrades(entities), new BulkUpdatePartialGradesStatusSuccess(entities)]);
            },
            (response: HttpErrorResponse) => {
                dispatch(new BulkUpdatePartialGradesStatusFailure(null));
            }
        );
    }
}
